import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import { useCrmCostSummary } from "@/hooks/use-crm-costs";
import { RunAgentsNowPanel } from "@/components/settings/crm/RunAgentsNowPanel";
import { useToggleSignalAgent } from "@/hooks/use-signal-agents";
import { failureShare, monthOptions } from "@/lib/crm-cost-calc";
import { formatNOK, formatPct } from "@/lib/format";
import { TONE_TEXT } from "@/lib/widget-status";

function KpiCard({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="kpi-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {note && <div className="delta">{note}</div>}
    </div>
  );
}

/** Kostnad for CRM-agentene (Apify, Claude og SMS) per måned, per agent og per kunde. Admin. */
export function CrmCostPanel() {
  const months = useMemo(() => monthOptions(new Date()), []);
  const [month, setMonth] = useState(months[0].value);
  const { data, isLoading, error } = useCrmCostSummary(month);
  const toggle = useToggleSignalAgent();
  const failures = data ? failureShare(data) : null;

  return (
    <section className="space-y-4" aria-label="Kostnader for CRM-agenter">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Kostnader</h2>
          <p className="text-sm text-muted-foreground">Apify, Claude og SMS. Prøvekjøringer er med, fordi de koster penger. Totalen er lagret kostnad per kjøring; Apify og Claude er beregnet med dagens satser.</p>
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-48" aria-label="Velg måned"><SelectValue /></SelectTrigger>
          <SelectContent>
            {months.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <p className={`text-sm ${TONE_TEXT.miss}`}>Kunne ikke hente kostnader: {(error as Error).message}</p>
      ) : isLoading || !data ? (
        <Skeleton className="h-40" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard label="Totalt" value={formatNOK(data.total_nok)} />
            <KpiCard label="Apify" value={formatNOK(data.apify_nok)} />
            <KpiCard label="Claude" value={formatNOK(data.claude_nok)} />
            <KpiCard label="SMS" value={formatNOK(data.sms_nok)} />
            <KpiCard label="Overvåkede kunder" value={String(data.monitored_customers)} />
            <KpiCard label="Kjøringer" value={String(data.runs)} />
            <KpiCard label="Feilandel" value={failures == null ? "–" : formatPct(failures * 100, 0)} note={`${data.failed_runs} feilet`} />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">Per agent</h3>
            {data.per_agent.length === 0 ? (
              <p className="text-sm text-muted-foreground">Ingen agenter har kjørt denne måneden.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead className="text-right">Kostnad</TableHead>
                    <TableHead className="text-right">Treff</TableHead>
                    <TableHead className="text-right">Tatt</TableHead>
                    <TableHead className="text-right">Avvist</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.per_agent.map((a) => (
                    <TableRow key={a.agent_id ?? a.name}>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell className="text-right">{formatNOK(a.cost_nok)}</TableCell>
                      <TableCell className="text-right">{a.signals}</TableCell>
                      <TableCell className="text-right">{a.claimed}</TableCell>
                      <TableCell className="text-right">{a.dismissed}</TableCell>
                      <TableCell>
                        {a.cost_cap_reached ? (
                          <StatusBadge tone="miss" label="Pauset: kostnadstak nådd" />
                        ) : a.is_active ? (
                          <StatusBadge tone="reached" label="Aktiv" />
                        ) : (
                          <StatusBadge tone="near" label="Pauset" />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {a.agent_id && a.is_active && (
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`Pause ${a.name}`}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate({ id: a.agent_id!, isActive: false })}
                          >
                            Pause
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">Dyreste kunder (topp 20)</h3>
            {data.per_customer.length === 0 ? (
              <p className="text-sm text-muted-foreground">Ingen kostnader på kunder denne måneden.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Kunde</TableHead><TableHead className="text-right">Kostnad</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {data.per_customer.map((c) => (
                    <TableRow key={c.customer_id}>
                      <TableCell>{c.name}</TableCell>
                      <TableCell className="text-right">{formatNOK(c.cost_nok)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}

      <RunAgentsNowPanel />
    </section>
  );
}
